/** Tipo de lead: debe coincidir con SMART.ERP.Domain.Enums.LeadType del API. */
export enum LeadType {
  Proyecto = 1,
  VentaInsumos = 2,
}

/** Estado de sistema de una etapa (SMART.ERP.Domain.Enums.LeadStageState). */
export enum LeadStageState {
  Abierto = 0,
  Ganado = 1,
  Perdido = 2,
}

export const LEAD_TYPE_LABELS: Record<LeadType, string> = {
  [LeadType.Proyecto]: 'Proyecto',
  [LeadType.VentaInsumos]: 'Venta de insumos',
};

export interface LeadStage {
  id: number;
  name: string;
  leadType: LeadType;
  order: number;
  state: LeadStageState;
  probabilityPercentage: number;
  staleAfterDays: number | null;
}

export interface LeadListItem {
  id: number;
  code: string;
  leadType: LeadType;
  contactName: string | null;
  contactPhone: string | null;
  contactPhoneDisplay: string | null;
  companyName: string | null;
  stageId: number;
  stageName: string | null;
  stageState: LeadStageState;
  nextActionAt: string | null;
  nextActionNote: string | null;
  lastContactAt: string | null;
  stageChangedAt: string | null;
  budget: number;
  userId: string;
  userName: string | null;
  customerId: string | null;
  customerName: string | null;
  originName: string | null;
  creationDate: string;
  isOverdue: boolean;
  isStale: boolean;
  /** Fecha de la cotización más reciente (cadencia de seguimiento). */
  quotedAt: string | null;
}

export interface LeadTimelineItem {
  id: number;
  message: string;
  userName: string | null;
  creationDate: string;
}

export interface LeadQuotation {
  id: number;
  quotationCode: string | null;
  total: number;
  statusName: string | null;
  creationDate: string;
  dueDate: string;
  viewCount: number;
  lastViewedAt: string | null;
}

export interface LeadActivity {
  id: number;
  typeName: string | null;
  description: string;
  initDate: string;
  endDate: string;
  statusName: string | null;
  isDone: boolean;
  userName: string | null;
}

export interface LeadDocument {
  id: number;
  name: string;
  url: string;
  typeName: string | null;
  creationDate: string;
}

export interface LeadDetail extends LeadListItem {
  contactEmail: string | null;
  description: string | null;
  typeOriginId: number | null;
  expectedClosingDate: string | null;
  closingDate: string | null;
  lossReasonId: number | null;
  lossReasonName: string | null;
  lossNote: string | null;
  stages: LeadStage[];
  timeline: LeadTimelineItem[];
  quotations: LeadQuotation[];
  activities: LeadActivity[];
  documents: LeadDocument[];
  /** JSON del levantamiento técnico (ver LeadSurvey). */
  surveyData: string | null;
  projectId: number | null;
  projectCode: string | null;
  sourceDetail: string | null;
  adHeadline: string | null;
}

export interface LeadBoardColumn {
  stage: LeadStage;
  count: number;
  totalBudget: number;
  leads: LeadListItem[];
}

export interface LeadMyDay {
  overdue: LeadListItem[];
  today: LeadListItem[];
  withoutNextAction: LeadListItem[];
  upcoming: LeadListItem[];
  staleCount: number;
  openCount: number;
}

export interface LeadPhoneCustomer {
  id: string;
  fullName: string;
  phoneNumber: string | null;
  rtn: string | null;
}

export interface LeadPhoneCheck {
  normalizedPhone: string | null;
  phoneDisplay: string | null;
  openLeads: LeadListItem[];
  customers: LeadPhoneCustomer[];
}

export interface QuickCreateLeadResult {
  created: boolean;
  lead: LeadDetail;
}

export interface LeadSeller {
  id: string;
  fullName: string;
  roleSelector: string | null;
}

export interface LeadCatalogItem {
  id: number;
  name: string;
  isActive: boolean;
}

export interface QuickCreateLead {
  phone: string;
  leadType: LeadType;
  contactName?: string | null;
  note?: string | null;
  typeOriginId?: number | null;
  customerId?: string | null;
  nextActionAt?: string | null;
  forceNew?: boolean;
}

export interface UpdateLead {
  id: number;
  leadType: LeadType;
  phone: string;
  contactName: string | null;
  contactEmail: string | null;
  companyName: string | null;
  budget: number;
  expectedClosingDate: string | null;
  description: string | null;
  typeOriginId: number | null;
  customerId: string | null;
}

export interface MoveLeadStage {
  id: number;
  stageId: number;
  lossReasonId?: number | null;
  lossNote?: string | null;
  nextActionAt?: string | null;
  nextActionNote?: string | null;
}

export interface LeadFollowUp {
  id: number;
  contacted: boolean;
  note?: string | null;
  nextActionAt?: string | null;
  nextActionNote?: string | null;
}

export interface ScheduleLeadVisit {
  id: number;
  visitAt: string;
  durationMinutes: number;
  assignedUserId?: string | null;
  notes?: string | null;
}

export interface CreateLeadProject {
  id: number;
  prefixId: number;
  customerId?: string | null;
  name?: string | null;
  startDate?: string | null;
}

/** Plantillas del levantamiento técnico. */
export type LeadSurveyTemplate = 'cctv' | 'red' | 'fibra';

export interface LeadSurvey {
  template: LeadSurveyTemplate;
  answers: Record<string, string | number | boolean | null>;
  updatedAt: string;
}

export interface LeadTypeMetrics {
  leadType: string;
  created: number;
  won: number;
  lost: number;
  winRate: number;
  wonAmount: number;
  medianDaysToWin: number | null;
  openNow: number;
  openPipeline: number;
  weightedPipeline: number;
}

export interface LeadCountRow {
  name: string;
  leads: number;
  won: number;
}

export interface LeadSellerMetrics {
  seller: string;
  open: number;
  overdue: number;
  withoutNextAction: number;
  won: number;
  lost: number;
}

export interface LeadMetrics {
  from: string;
  to: string;
  byType: LeadTypeMetrics[];
  byOrigin: LeadCountRow[];
  lossReasons: LeadCountRow[];
  bySeller: LeadSellerMetrics[];
}

/** Orígenes que asigna el job de recompra del API (RepurchaseKinds). */
export const REPURCHASE_ORIGIN = 'Recompra';
export const REACTIVATION_ORIGIN = 'Reactivación';

/** Cliente con compras repetidas al que le toca recomprar ("Recompra") o que dejó de comprar ("Reactivación"). */
export interface RepurchaseCandidate {
  customerId: string;
  customerName: string;
  phone: string | null;
  sellerId: string | null;
  kind: typeof REPURCHASE_ORIGIN | typeof REACTIVATION_ORIGIN;
  /** Días distintos con compra en los últimos 18 meses. */
  purchaseDays: number;
  /** Días típicos entre compras (mediana). */
  cycleDays: number;
  lastPurchase: string;
  daysSinceLastPurchase: number;
  averageTicket: number;
  topProducts: string[];
  /** Ya tiene un lead abierto o ya se le creó el de recompra después de su última compra. */
  alreadyHandled: boolean;
  openLeadId: number | null;
}

export interface RepurchaseRunResult {
  candidates: number;
  created: number;
  skipped: number;
  leadIds: number[];
}
